import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, AlertCircle, Zap } from "lucide-react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface FineTuneJob {
  id: string;
  job_id: string;
  status: string;
  num_examples_trained: number | null;
  model_deployment_name: string | null;
  booking_rate_before: number | null;
  booking_rate_after: number | null;
  ab_test_percentage: number | null;
  started_at: string | null;
  completed_at: string | null;
}

const StatusIcon = ({ status }: { status: string }) => {
  if (status === "completed")
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
  if (status === "running")
    return <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />;
  if (status === "failed")
    return <AlertCircle className="w-3.5 h-3.5 text-rose-400" />;
  return <Loader2 className="w-3.5 h-3.5 text-slate-300" />;
};

const elapsed = (start: string | null): string => {
  if (!start) return "";
  const sec = Math.round((Date.now() - new Date(start).getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
};

export const FineTuneStatus = () => {
  const [jobs, setJobs] = useState<FineTuneJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const supabase = getSupabaseClient();
    const { data, error: err } = await supabase
      .from("fine_tune_jobs")
      .select(
        "id,job_id,status,num_examples_trained,model_deployment_name,booking_rate_before,booking_rate_after,ab_test_percentage,started_at,completed_at",
      )
      .order("started_at", { ascending: false })
      .limit(5);
    if (err) {
      setError(err.message);
    } else {
      setJobs((data as FineTuneJob[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const id = window.setInterval(load, 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-[#E8ECF0] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-4 h-4 text-amber-400" />
        <h2 className="text-[13px] font-semibold text-[#0F1629]">
          Fine-Tune Jobs
        </h2>
        <span className="ml-auto text-[10px] text-slate-400">
          auto-refreshes every 30s
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-[12px] text-slate-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading…
        </div>
      ) : error ? (
        <div className="py-4 text-[12px] text-rose-400">
          Could not load fine-tune jobs. Table may not exist yet.
        </div>
      ) : jobs.length === 0 ? (
        <div className="py-6 text-center">
          <Zap className="w-6 h-6 text-slate-200 mx-auto mb-2" />
          <p className="text-[12px] text-slate-400">
            No fine-tune jobs yet. Sera will start training once enough calls
            are collected.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="rounded-xl border border-[#E8ECF0] p-3.5 space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <StatusIcon status={job.status} />
                  <span className="text-[12px] font-semibold text-[#0F1629] capitalize">
                    {job.status}
                  </span>
                  {job.model_deployment_name && (
                    <span className="text-[10px] text-slate-400 font-mono">
                      {job.model_deployment_name}
                    </span>
                  )}
                </div>
                {job.ab_test_percentage != null && (
                  <span className="text-[10px] text-slate-500">
                    A/B: {job.ab_test_percentage}% traffic
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div>
                  <p className="text-slate-400">Examples</p>
                  <p className="font-semibold text-[#0F1629]">
                    {job.num_examples_trained ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Before</p>
                  <p className="font-semibold text-[#0F1629]">
                    {job.booking_rate_before != null
                      ? `${job.booking_rate_before}%`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">After</p>
                  <p
                    className="font-semibold"
                    style={{
                      color:
                        job.booking_rate_after != null &&
                        job.booking_rate_before != null &&
                        job.booking_rate_after > job.booking_rate_before
                          ? "#1A8C5B"
                          : "#0F1629",
                    }}
                  >
                    {job.booking_rate_after != null
                      ? `${job.booking_rate_after}%`
                      : "—"}
                  </p>
                </div>
              </div>

              {job.started_at && (
                <p className="text-[10px] text-slate-400">
                  {job.status === "running"
                    ? `Running · ${elapsed(job.started_at)} elapsed`
                    : `Completed · ${new Date(job.completed_at ?? job.started_at).toLocaleDateString()}`}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
